import { EditorState } from 'draft-js';
import { useParams } from 'react-router';

import { editorStateToHtml } from '../../../../foundation/components/rich_text_editor/RichTextEditor';
import { useModalForm } from '../../../../foundation/hooks';
import {
  useGetSegregationOptionsQuery,
  useGetZygosityOptionsQuery,
} from '../../../api/globalDataApi';
import {
  useAddNewSampleMutation,
  useGetVariantDetailQuery,
} from '../../../api/variantApi';

interface FormValues {
  sample_id?: string;
  zygosity?: string;
  segregation?: string;
  comment?: EditorState;
}

interface UseSampleModalLogicProps {
  closeModal: () => void;
}

/**
 * Custom hook that encapsulates all the business logic for SampleModal.
 * Separates concerns by extracting state management, selectors, and handlers
 * from the UI component.
 */
export const useSampleModalLogic = ({
  closeModal,
}: UseSampleModalLogicProps) => {
  const { data: zygosityOptions = [] } = useGetZygosityOptionsQuery();
  const { data: segregationOptions = [] } = useGetSegregationOptionsQuery();
  const { vid } = useParams();
  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: vid },
    { skip: !vid },
  );

  // Mutation hook returns a [trigger, result] tuple.
  // After a successful save, invalidatesTags for VariantSamples fires and RTK
  // Query refetches the samples list automatically
  const [addNewSampleMutation] = useAddNewSampleMutation();

  const { isLoading, user, handleModalClose, handleSubmit, createModalOpts } =
    useModalForm({
      onClose: closeModal,
      successMessage: 'Sample created successfully',
    });
  const isDiagnosticumUser = user?.institution?.id === 1;

  const modalOpts = {
    ...createModalOpts('Create Sample'),
  };

  const initialValues: FormValues = {
    sample_id: undefined,
    zygosity: undefined,
    segregation: undefined,
    comment: EditorState.createEmpty(),
  };

  const handleFormSubmit = async (values: FormValues) => {
    const sampleId = String(values.sample_id ?? '').trim();

    await handleSubmit(async () => {
      if (variant?.id) {
        await addNewSampleMutation({
          variant_id: variant.id,
          requestData: {
            ...values,
            sample_id: sampleId,
            comment: editorStateToHtml(
              values.comment ?? EditorState.createEmpty(),
            ),
          },
        }).unwrap();
      }
    });
  };

  return {
    isLoading,
    user,
    isDiagnosticumUser,
    variant,
    zygosityOptions,
    segregationOptions,
    modalOpts,
    initialValues,
    handleModalClose,
    handleFormSubmit,
  };
};

export default useSampleModalLogic;
